export type WizardBranchOutcome = 'active' | 'failed' | 'completed'

export interface WizardBranchObservation {
  branchId?: string
  outcome: WizardBranchOutcome
}

export interface WizardBranchSwitch {
  shouldSwitch: boolean
  branchId?: string
}

interface AdoptedBranch {
  branchId: string
  fromBranchId?: string
}

const runsStartedHere = new Set<string>()
const adoptedBranches = new Map<string, AdoptedBranch>()

/** Marks an explicit start/resume in this browser tab as eligible to move its branch. */
export function markIngestWizardRunStartedHere(runId: string): void {
  runsStartedHere.add(runId)
}

/**
 * Atomically decides whether one observer should move the active branch.
 *
 * StepRun and the global coordinator can observe the same durable run. The
 * module-level claim preserves the "begun here" ownership rule and prevents
 * duplicate switches across both observers, remounts, and polling responses.
 */
export function resolveIngestWizardBranchSwitch(input: {
  runId: string
  activeBranchId?: string
  observation: WizardBranchObservation
}): WizardBranchSwitch {
  const { runId, activeBranchId, observation } = input

  if (observation.outcome === 'failed') {
    runsStartedHere.delete(runId)
    const adopted = adoptedBranches.get(runId)
    adoptedBranches.delete(runId)
    if (adopted && activeBranchId === adopted.branchId && adopted.fromBranchId !== activeBranchId) {
      return { shouldSwitch: true, branchId: adopted.fromBranchId }
    }
    return { shouldSwitch: false }
  }

  let next: WizardBranchSwitch = { shouldSwitch: false }
  if (observation.branchId && runsStartedHere.has(runId) && !adoptedBranches.has(runId)) {
    adoptedBranches.set(runId, {
      branchId: observation.branchId,
      ...(activeBranchId ? { fromBranchId: activeBranchId } : {})
    })
    next = {
      shouldSwitch: observation.branchId !== activeBranchId,
      branchId: observation.branchId
    }
  }

  if (observation.outcome === 'completed') {
    runsStartedHere.delete(runId)
    adoptedBranches.delete(runId)
  }
  return next
}

export function forgetIngestWizardRun(runId: string): void {
  runsStartedHere.delete(runId)
  adoptedBranches.delete(runId)
}

export function resetIngestWizardBranchAdoption(): void {
  runsStartedHere.clear()
  adoptedBranches.clear()
}
